import { FC } from 'react';
import { SidePanel } from '@carbon/ibm-products';
import { SidePanelResizeHandle } from 'components/resizable-side-panel';
import { useExclusiveDrawer } from './exclusive-drawer-hooks';
import 'components/inline-edit';
import style from './scoring-glossary-panel.module.scss';

interface Props {
  open: boolean;
  onClose: () => void;
}

interface Factor {
  name: string;
  weight: string;
  description: string;
}

const FACTORS: Factor[] = [
  {
    name: 'Name Similarity',
    weight: '45% of score',
    description:
      'How closely the candidate’s name matches the extracted name — including exact matches, abbreviations (e.g., Sam A. vs. Sam Arthur), name-order variations, and known aliases.',
  },
  {
    name: 'Entity Type Alignment',
    weight: '10% of score',
    description:
      'Whether the extracted name’s type matches the candidate’s. Keywords like LLC, Inc., Trust, Partnership, or Estate signal an Organization; absence usually suggests an Individual.',
  },
  {
    name: 'Geographic & Citation Context',
    weight: '15% of score',
    description:
      'Whether the candidate has prior documents or citations in the same county as this document. Same county with multiple citations scores highest; same state only scores moderately.',
  },
  {
    name: 'Legal Description Overlap',
    weight: '15% of score',
    description:
      'Whether the candidate appears in prior documents involving the same subdivision, survey, or property boundaries as this document. Exact-match subdivision scores highest.',
  },
  {
    name: 'Address Similarity',
    weight: '10% of score',
    description:
      'How closely the candidate’s address matches the extracted entity’s. Same street scores highest; same city/state scores moderately. If the candidate has no address on file, this factor shows “no data”.',
  },
  {
    name: 'Cross-Party Context',
    weight: '5% of score',
    description:
      'Whether other parties on this document (grantors, grantees, trustees) also appear in the candidate’s document history — a signal of related-party connection.',
  },
];

export const ScoringGlossaryPanel: FC<Props> = ({ open, onClose }) => {
  const pageContentSelector = useExclusiveDrawer(
    'scoring-glossary',
    open,
    onClose
  );
  return (
    <>
      <SidePanelResizeHandle
        open={open}
        panelSelector=".scoring-glossary-sidepanel"
        pageContentSelector={pageContentSelector}
        defaultWidthPx={480}
      />
      <SidePanel
        open={open}
        onRequestClose={onClose}
        title="Scoring Glossary"
        subtitle="How each match suggestion is scored."
        className={`scoring-glossary-sidepanel ${style.sidePanel}`}
        slideIn
        selectorPageContent={pageContentSelector}
        placement="right"
        size="md"
      >
        <div className={style.body}>
          <section className={style.section}>
            <h4 className={style.heading}>Confidence</h4>
            <p className={style.text}>
              The recorded confidence for this match, on a 0–100% scale.
              Candidates must score above the configured threshold to be kept as
              suggestions; when none do, the party shows “no match”.
            </p>
          </section>

          <hr className={style.divider} />

          <section className={style.section}>
            <h4 className={style.heading}>How the confidence score is built</h4>
            <p className={style.text}>
              Each candidate is scored against six factors, each weighted by how
              much it contributes to the final score. The Weight column shows
              each factor’s share; the Score column shows this candidate’s
              weighted contribution. All weights sum to 100%.
            </p>
            <div className={style.formula}>
              <span className={style.formulaLabel}>Formula</span>
              <code className={style.formulaText}>
                0.45·name + 0.10·type + 0.15·geo + 0.15·legal + 0.10·addr +
                0.05·cross
              </code>
            </div>
          </section>

          <hr className={style.divider} />

          <section className={style.section}>
            <h4 className={style.factorsHeading}>The Six Factors</h4>
            <dl className={style.factors}>
              {FACTORS.map((factor) => (
                <div key={factor.name} className={style.factor}>
                  <div className={style.factorHeader}>
                    <dt className={style.factorName}>{factor.name}</dt>
                    <span className={style.factorWeight}>{factor.weight}</span>
                  </div>
                  <dd className={style.factorDesc}>{factor.description}</dd>
                </div>
              ))}
            </dl>
          </section>
        </div>
      </SidePanel>
    </>
  );
};
